/**
 * Identity: where it sits (line › line section), its name with class and status, one sentence
 * on what it is, and badges with its key figures. The same in every lens.
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs, CopyButton, PageHeader, StatusBadge, Tag } from '@bn-apps/dim-ui-react';
import type { EntityViewModel } from '@hooks/useEntityView';
import { uriToDereferencePath } from '@utils/prefix.utils';
import { DASH, DVIEW, pickLang, setting, type ViewPropertyShape } from '@utils/viewShapes.utils';
import { rowCount, valuesOf, type Validity } from '../page';
import { coverage, formatKm, localName, type LinearLocation } from '../linearLocation';
import { aggregate, useNumberFormat } from '../widgets/figures';
import { conceptOf, shortName } from '@services/vocabulary.service';
import { recordName, UriLink } from '../widgets/values';
import { kmOf } from './Graph';
import { Chip } from '../widgets/chip';
import type { WidgetContext } from '../widgets/types';
import styles from '../EntityTemplate.module.scss';

/** A short label such as "1" reads as "Track 1". */
export function pageTitle(label: string, className: string | undefined): string {
  return className && label.length <= 6 && !label.toLowerCase().startsWith(className.toLowerCase())
    ? `${className} ${label}`
    : label;
}

/**
 * The object's key figures: the rows with dview:badge as pills, in its order. The header shows them
 * under the title and the hero above its drawing, the same for every class.
 */
export function useKeyFacts(model: EntityViewModel, context: WidgetContext, stretch: LinearLocation | null) {
  const { t } = useTranslation();
  const format = useNumberFormat();
  const language = context.language;
  const marked = (name: string) =>
    model.allProperties.filter((row) => setting(row, name)).sort((a, b) => Number(setting(a, name)) - Number(setting(b, name)));
  const linkText = (row: ViewPropertyShape, iri: string) =>
    recordName(iri, context, row.classIri ? (context.resolved[row.classIri]?.[0] ?? localName(row.classIri)) : null);

  // What a row amounts to: a lane's coverage, a number, or how many it has.
  const figure = (row: ViewPropertyShape | undefined): { value: number; text: string; word?: boolean; iri?: string } | null => {
    if (!row) return null;
    const lane = stretch?.lanes.find((candidate) => candidate.key === row.key);
    if (lane?.kind === 'bands' && stretch) {
      if (lane.segments.length === 0) return null;
      const share = Math.round(coverage(lane, stretch) * 100);
      return { value: share, text: String(share) };
    }
    const values = valuesOf(model, row);
    const number = aggregate(row, values);
    // A from km with a to km (dview:kmTo) reads as the range, km 51.794–52.248
    const toKey = setting(row, 'kmTo');
    const to = toKey ? aggregate(row, valuesOf(model, model.rows[toKey])) : null;
    if (number !== null && row.viewer === DVIEW.KmViewer && to !== null) return { value: number, text: `${formatKm(Math.min(number, to))}–${formatKm(Math.max(number, to))}` };
    if (number !== null && row.viewer === DVIEW.KmViewer) return { value: number, text: formatKm(number) };
    if (number !== null) return { value: number, text: format(number, setting(row, 'unit') === 'km' ? 2 : 0) };
    // Hidden rows and tables are collections (signals, platform edges): they count
    if (!row.hidden && !model.tables[row.id] && values[0]) {
      const first = values[0];
      // A value that is a concept's notation reads as the concept's name, without its paragraph number
      const concept = conceptOf(context.concepts, setting(row, 'scheme'), first.value);
      const name = concept ? pickLang(concept.label, language) : null;
      if (concept && name) return { value: 1, text: shortName(concept, name), word: true };
      const answer = first.value.toLowerCase();
      if (row.viewer === DVIEW.YesNoViewer && (answer === 'yes' || answer === 'no')) return { value: 1, text: t(`entity.template.yesNo.${answer}`), word: true };
      const symbol = setting(row, 'symbol');
      if (first.type === 'uri') return { value: 1, text: linkText(row, first.value), iri: first.value };
      return { value: 1, text: symbol ? t(`entity.template.symbol.${symbol}.${first.value}`, first.value) : first.value, word: Boolean(symbol) };
    }
    const count = row.key ? rowCount(model, row.key) : 0;
    return count > 0 ? { value: count, text: String(count) } : null;
  };

  // Badges: the rows with dview:badge, in its order; the value in bold.
  const MARK = '\u0000';
  const badges = marked('badge').flatMap((row) => {
    const found = figure(row);
    if (!found) return [];
    const name = pickLang(row.name, language) ?? row.key ?? '';
    const unit = found.iri || found.word ? '' : (setting(row, 'unit') ?? '');
    const text = t(`entity.template.badge.${row.key}`, {
      count: found.value,
      value: MARK,
      defaultValue: `${name.charAt(0).toUpperCase()}${name.slice(1)} ${MARK}${unit ? ` ${unit}` : ''}`,
    });
    const [before, after] = text.split(MARK);
    const content = (
      <>
        {before}
        {after !== undefined && <b>{found.text}</b>}
        {after}
      </>
    );
    // A badge that names another record (the line, the line section) is the same pill as in the facts
    if (found.iri) return [<Chip key={row.id} iri={found.iri} icon={setting(row, 'icon')} label={found.text} context={context} />];
    // The kind in the colour the structure has in the drawing (a bridge teal, a tunnel grey)
    const kind = setting(row, 'symbol') === 'structure' ? valuesOf(model, row)[0]?.value : undefined;
    return [
      <span key={row.id} className={styles.badge} data-kind={kind}>
        {content}
      </span>,
    ];
  });
  return { figure, badges };
}

/**
 * Above the hero: the object's name and its measures only (the rows with dview:keyFact: a length, a
 * height, a km), not the whole row of pills under the title.
 */
export function KeyFacts({ model, context, stretch, title }: { model: EntityViewModel; context: WidgetContext; stretch: LinearLocation | null; title: string }): JSX.Element {
  const { figure } = useKeyFacts(model, context, stretch);
  const measures = model.allProperties
    .filter((row) => setting(row, 'keyFact') === 'true')
    .sort((a, b) => a.order - b.order)
    .flatMap((row) => {
      const found = figure(row);
      if (!found || found.iri || found.word) return [];
      const unit = row.viewer === DVIEW.KmViewer ? '' : (setting(row, 'unit') ?? '');
      return [`${row.viewer === DVIEW.KmViewer ? 'km ' : ''}${found.text}${unit ? ` ${unit}` : ''}`];
    });
  // An object placed on a track without a km of its own (dview:at empty) has its km from the
  // topology, in its spot row's table: the same km the drawing puts it at
  // Only an object at one spot: a tunnel or a bridge lies along several tracks
  const spread = model.allProperties.some((row) => row.viewer === DVIEW.AreaLocationViewer || row.viewer === DVIEW.LinearLocationViewer);
  const spot = spread ? undefined : model.allProperties.find((row) => row.viewer === DVIEW.SpotViewer || row.viewer === DVIEW.SpotLocationViewer);
  if (spot && !measures.some((measure) => measure.startsWith('km '))) {
    const own = model.tables[spot.id]?.items.find((item) => item.iri === model.uri);
    const km = own ? kmOf(own.cells) : null;
    if (km !== null) measures.push(`km ${formatKm(km)}`);
  }
  // An object on a track names its track, as a link (the drawing shows only part of it)
  const trackRow = spot ? model.rows[setting(spot, 'track') ?? ''] : undefined;
  const track = trackRow ? valuesOf(model, trackRow).find((value) => value.type === 'uri')?.value : undefined;
  return (
    <p className={styles.keyFacts}>
      <b>{title}</b>
      {measures.map((measure, index) => (
        <span key={`${index}-${measure}`}>{measure}</span>
      ))}
      {track && (
        <span>
          <UriLink uri={track} context={context}>
            {recordName(track, context, trackRow?.classIri ? (context.resolved[trackRow.classIri]?.[0] ?? localName(trackRow.classIri)) : null)}
          </UriLink>
        </span>
      )}
    </p>
  );
}

interface IdentityProps {
  viewSwitch?: ReactNode;
  model: EntityViewModel;
  label: string;
  context: WidgetContext;
  validity: Validity;
  stretch: LinearLocation | null;
  warnings: number;
}

const Identity = ({ model, label, context, validity, stretch, warnings, viewSwitch }: IdentityProps): JSX.Element => {
  const { t } = useTranslation();
  const language = context.language;

  const className = model.nodeShapes.map((shape) => pickLang(shape.label, language)).find(Boolean);
  const title = pageTitle(label, className ?? undefined);
  const marked = (name: string) =>
    model.allProperties.filter((row) => setting(row, name)).sort((a, b) => Number(setting(a, name)) - Number(setting(b, name)));

  // A linked record's name; a short label such as "1" reads as "Track 1", by the row's sh:class.
  const linkText = (row: ViewPropertyShape, iri: string) =>
    recordName(iri, context, row.classIri ? (context.resolved[row.classIri]?.[0] ?? localName(row.classIri)) : null);

  // Breadcrumbs: the rows with dview:crumb, in its order.
  const crumbs = [
    ...marked('crumb').flatMap((row) => {
      const value = valuesOf(model, row).find((object) => object.type === 'uri');
      if (!value) return [];
      const text = linkText(row, value.value);
      return [{ label: t(`entity.template.crumb.${row.key}`, { value: text, defaultValue: text }), href: uriToDereferencePath(value.value) }];
    }),
    { label: title },
  ];

  const { figure, badges } = useKeyFacts(model, context, stretch);

  // The sentence: the class's template parts in the translations, each kept when the rows behind its {{slots}} have a value.
  const classKey = localName(model.nodeShapes[0]?.targetClasses[0] ?? '');
  const parts = t(`entity.template.summary.${classKey}`, { returnObjects: true, defaultValue: [] }) as string[] | string;
  const summary = (Array.isArray(parts) ? parts : [])
    .map((part) => {
      let complete = true;
      const text = part.replace(/\{\{(\w[\w-]*)\}\}/g, (_, key: string) => {
        const found = figure(model.rows[key]);
        if (!found) complete = false;
        return found?.word ? found.text.toLowerCase() : (found?.text ?? '');
      });
      return complete ? text : '';
    })
    .join('')
    .replace(/^./, (letter) => letter.toUpperCase());
  // Without a sentence for the class, its description (dash:DescriptionRole) says what it is
  const descriptionRow = model.allProperties.find((row) => row.role === DASH.DescriptionRole);
  const descriptions = descriptionRow ? (model.values[descriptionRow.id] ?? []) : [];
  const description = (descriptions.find((value) => value.lang && context.language.startsWith(value.lang)) ?? descriptions[0])?.value ?? '';
  // The object's own description leads; without one, the summary, and the class's description quieter
  const sentence = description || summary;
  const classDescription = description ? null : pickLang(model.nodeShapes[0]?.description ?? {}, language);

  return (
    <PageHeader
      className={styles.identity}
      title={title}
      breadcrumbs={
        <span className={styles.crumbRow}>
          <Breadcrumbs items={crumbs} />
          {viewSwitch}
        </span>
      }
      meta={
        <span className={styles.identityMeta}>
          {className && <Tag tone="info">{className}</Tag>}
          <StatusBadge
            status={validity.status === 'inUse' ? 'success' : validity.status === 'planned' ? 'info' : 'neutral'}
            title={validity.from ? t('entity.template.status.since', { date: validity.from.slice(0, 10) }) : undefined}
          >
            {t(`entity.template.status.${validity.status}`)}
          </StatusBadge>
        </span>
      }
      lead={
        <span className={styles.identityLead}>
          <span className={styles.identityIri}>
            <code>{model.uri}</code>
            <CopyButton text={model.uri} label={t('entity.template.about.copy')} copiedLabel={t('entity.template.about.copied')} size="xs" hideText variant="tertiary" />
          </span>
          {sentence && <Clamped text={sentence} />}
          {classDescription && (
            <span className={styles.classDescription}>
              {className ? `${className}: ` : ''}
              {classDescription}
            </span>
          )}
          <span className={styles.badges}>
            {badges}
            {warnings > 0 && (
              <span className={styles.badge} data-tone="warning">
                {t('entity.template.chips.warnings', { count: warnings })}
              </span>
            )}
          </span>
        </span>
      }
    />
  );
};

export default Identity;

/** A description clamped to two lines, with "more" when it is longer. */
function Clamped({ text }: { text: string }): JSX.Element {
  const { t } = useTranslation();
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  useLayoutEffect(() => {
    const element = ref.current;
    if (element) setLong(element.scrollHeight > element.clientHeight + 1);
  }, [text]);
  return (
    <span className={styles.leadText}>
      <span ref={ref} className={open ? undefined : styles.clamped}>
        {text}
      </span>
      {(long || open) && (
        <button type="button" className={styles.more} aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? t('entity.template.less') : t('entity.template.more')}
        </button>
      )}
    </span>
  );
}
